import { useState } from 'react';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

const milestones = [
  { year: '2018', title: '平台正式上线', desc: '17c.com正式面向公众开放，为用户提供稳定的访问服务。' },
  { year: '2019', title: '用户突破100万', desc: '凭借优质服务和良好口碑，注册用户数突破100万。' },
  { year: '2020', title: '安全体系升级', desc: '引入多层安全防护机制，全面升级数据加密和隐私保护。' },
  { year: '2021', title: '全球节点部署', desc: '完成全球CDN加速节点部署，访问速度大幅提升。' },
  { year: '2022', title: '移动端全面适配', desc: '完成移动端全面适配，支持手机和平板设备的流畅访问。' },
  { year: '2023', title: '社区功能上线', desc: '推出用户社区功能，搭建用户交流与分享平台。' },
  { year: '2024', title: '用户突破500万', desc: '注册用户突破500万，成为行业领先的访问服务平台。' },
  { year: '2025', title: '智能推荐上线', desc: '基于AI算法推出个性化内容推荐，提升用户体验。' },
];

const userReviews = [
  { name: '张先生', role: '资深用户 3年', content: '17c.com是我每天必用的平台，地址稳定、访问流畅，客服响应也非常及时。强烈推荐！', rating: 5 },
  { name: '李女士', role: '企业用户', content: '作为企业用户，17c.com的专业服务和安全性让我们非常放心。数据加密和隐私保护做得很好。', rating: 5 },
  { name: '王同学', role: '新用户 6个月', content: '刚开始使用17c.com时有些不太熟悉，但看了使用指南很快就上手了。界面简洁，体验很好。', rating: 4 },
  { name: '赵总', role: '合作伙伴', content: '和17c.com合作多年，平台的稳定性和用户服务一直保持高水准，是非常值得信赖的伙伴。', rating: 5 },
  { name: '陈女士', role: '老用户 5年', content: '使用17c.com已经5年了，见证了这个平台的成长和进步。功能越来越完善，体验越来越好。', rating: 5 },
  { name: '刘先生', role: '推荐用户', content: '朋友推荐来的，使用后非常满意。特别是备用入口的功能，在网络不稳定的情况下非常实用。', rating: 4 },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: '17c.com',
      url: 'https://17c.com',
      description: siteInfo.description,
      email: siteInfo.email,
      telephone: siteInfo.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '科技园南路17号',
        addressLocality: '深圳',
        addressRegion: '广东',
        addressCountry: 'CN',
      },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '09:00',
        closes: '22:00',
      },
    },
    {
      '@type': 'WebPage',
      name: '关于我们 - 17c.com',
      url: 'https://17c.com/about',
      description: '了解17c.com的品牌故事、发展历程、用户评价及联系方式',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://17c.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://17c.com/about' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '17c.com 提供什么服务？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '17c.com是一个专业的访问服务平台，提供最新官方地址入口、安全访问指南、使用教程、常见问题解答以及内容导航服务。',
          },
        },
        {
          '@type': 'Question',
          name: '如何联系 17c.com 客服？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `您可以通过邮箱 ${siteInfo.email}、电话 ${siteInfo.phone} 或访问官网关于我们页面联系客服。工作时间：${siteInfo.workingHours}。`,
          },
        },
      ],
    },
  ],
};

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value || '';
    if (honeypot.trim()) {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '' });
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9phj46b4qjhggaepuj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '发送失败，请稍后重试';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 17c.com 品牌介绍与联系方式"
        description="了解17c.com的品牌故事、发展历程、核心价值观及联系方式。17c.com致力于为用户提供安全稳定的访问服务。"
        keywords="17c.com品牌介绍,17c.com联系方式,17c.com发展历程,关于17c.com"
        canonicalUrl="https://17c.com/about"
      />
      <SchemaOrg data={aboutSchema} id="about" />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
              <div className="pb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  关于我们
                </h1>
                <p className="text-foreground-600 text-sm">
                  了解 {siteInfo.name} 的品牌故事与发展历程
                </p>
              </div>
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                    品牌介绍
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                    致力于提供安全稳定的访问服务
                  </h2>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    {siteInfo.name} 成立于2018年，经过{siteInfo.stats.yearsOnline}年的发展，已成长为行业领先的访问服务平台。我们始终秉持"用户至上、安全第一"的理念，为数百万用户提供稳定、安全、流畅的访问体验。
                  </p>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    我们深知用户对访问稳定性和安全性的重视。为此，{siteInfo.name} 持续投入资源优化基础设施，部署全球CDN加速节点，建立多层安全防护机制，确保每一位用户都能获得优质的访问服务。
                  </p>
                  <p className="text-foreground-700 leading-relaxed">
                    目前，{siteInfo.name} 已累计服务超过 <strong className="text-primary-600">{siteInfo.stats.totalUsers}</strong> 用户，日均访问量达 <strong className="text-primary-600">{siteInfo.stats.dailyVisits}</strong>，用户满意度高达 <strong className="text-primary-600">{siteInfo.stats.satisfactionRate}</strong>。
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20professional%20team%20collaborating%20in%20a%20bright%20minimalist%20office%20space%20with%20technology%20elements%2C%20warm%20orange%20and%20teal%20accent%20decor%2C%20clean%20and%20welcoming%20atmosphere&width=800&height=550&seq=about-brand-01&orientation=landscape"
                    alt="17c.com 团队办公环境"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-14 bg-primary-500">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-1">
                {[
                  { value: siteInfo.stats.totalUsers, label: '累计用户' },
                  { value: siteInfo.stats.dailyVisits, label: '日均访问量' },
                  { value: siteInfo.stats.yearsOnline, label: '年稳定运行' },
                  { value: siteInfo.stats.satisfactionRate, label: '用户满意度' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center py-8">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-background-50/80">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-16 md:py-24 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-14">
                <span className="inline-block bg-accent-50 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  发展历程
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  一路走来，感恩有你
                </h2>
              </div>

              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((item, index) => (
                    <div
                      key={item.year}
                      className={`relative flex items-start gap-6 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="hidden md:block md:w-1/2"></div>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-100"></div>
                      <div className="ml-10 md:ml-0 md:w-1/2 md:px-8">
                        <div className="bg-background-50 rounded-lg p-5">
                          <span className="text-xs font-bold text-accent-600 bg-accent-50 px-2.5 py-1 rounded-full">
                            {item.year}
                          </span>
                          <h4 className="font-heading text-base font-semibold text-foreground-900 mt-3 mb-1.5">
                            {item.title}
                          </h4>
                          <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* User Reviews */}
          <section className="py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-14">
                <span className="inline-block bg-secondary-100 text-secondary-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  用户声音
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  听听用户怎么说
                </h2>
                <p className="text-foreground-600 max-w-xl mx-auto">
                  来自真实用户的评价，是我们前进的最大动力
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {userReviews.map((review) => (
                  <div key={review.name} className="bg-background-100/70 rounded-lg p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${
                            i < review.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      "{review.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 font-bold text-xs">
                        {review.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                        <div className="text-xs text-foreground-500">{review.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="py-16 md:py-24 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-14">
                <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  随时为您提供帮助
                </h2>
                <p className="text-foreground-600 max-w-xl mx-auto">
                  如果您有任何问题或建议，欢迎通过以下方式联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-4">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: siteInfo.email, href: `mailto:${siteInfo.email}` },
                    { icon: 'ri-phone-line', label: '客服热线', value: siteInfo.phone, href: `tel:${siteInfo.phone}` },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: siteInfo.address },
                    { icon: 'ri-time-line', label: '工作时间', value: siteInfo.workingHours },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">{item.label}</div>
                        {item.href ? (
                          <a href={item.href} className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                            {item.value}
                          </a>
                        ) : (
                          <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="lg:col-span-2">
                  <div className="bg-background-50 rounded-lg p-6">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                      发送消息
                    </h3>
                    <form onSubmit={handleSubmit} data-readdy-form="">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                        aria-hidden="true"
                        className="form-honeypot"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            您的姓名
                          </label>
                          <input
                            type="text"
                            id="contact-name"
                            name="name"
                            required
                            value={contactForm.name}
                            onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                            className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            电子邮箱
                          </label>
                          <input
                            type="email"
                            id="contact-email"
                            name="email"
                            required
                            value={contactForm.email}
                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                            className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div className="mb-4">
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        />
                        <div className="text-xs text-foreground-400 mt-1 text-right">
                          {contactForm.message.length}/500
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer"
                      >
                        发送消息
                      </button>
                      {formStatus === 'success' && (
                        <span className="ml-3 text-sm text-green-600">消息已发送，感谢您的反馈！</span>
                      )}
                      {formStatus === 'error' && (
                        <span className="ml-3 text-sm text-red-500">{formError || '发送失败，请稍后重试'}</span>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}